import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { MIN_PAYMENT, validateRequest } from '@78/core';
import { CURRENCY, formatAmount } from '@78/i18n';
import { createRequest, fetchRequestPreview } from '../lib/api.ts';
import { useActions } from '../lib/actions.ts';
import { getInitData, openExternal } from '../lib/telegram.ts';
import { friendlyAddress, useCollectionNames, shortAddress } from '../lib/collections.ts';
import { useErrorText } from '../lib/errors.ts';
import { useFmt } from '../lib/format.tsx';

/** Цены человек называет целыми монетами, контракт держит нанотоны. */
const TON_UNIT = 1_000_000_000n;

/**
 * Запрос на рассрочку.
 *
 * Покупатель называет цену и график — и только их. Уровень возврата,
 * переносы и льготный период назначает продавец: это его риск, и торговать
 * им покупателю нечем.
 */
export function RequestForm({
  collection,
  item = null,
  onDone,
}: {
  collection: string;
  /**
   * Вещь, с карточки которой пришли. Без неё запрос уходит на коллекцию:
   * ответить может любой держатель, и шансов получить ответ больше. С ней
   * письмо уходит владельцу лично — и всё равно ещё и по коллекции, чтобы
   * молчание одного человека не хоронило запрос.
   */
  item?: { address: string; name: string | null } | null;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const { sum, money, duration, lang } = useFmt();
  const actions = useActions();
  const errorText = useErrorText();
  const nameOf = useCollectionNames();
  const qc = useQueryClient();

  const [price, setPrice] = useState(1000);
  const [downPct, setDownPct] = useState(30);
  const [payments, setPayments] = useState(6);
  const [weeks, setWeeks] = useState(1);

  const total = BigInt(price) * TON_UNIT;
  const upfront = (total * BigInt(downPct)) / 100n;
  const period = weeks * 7 * 86400;
  const installment = payments > 0 ? (total - upfront) / BigInt(payments) : 0n;

  const problem = validateRequest({ totalPrice: total, upfront, period, nPayments: payments });

  // Превью письма продавцу. Запрашивается только на годных условиях: на
  // негодных запрос всё равно не уйдёт, а дёргать сервер на каждое движение
  // ползунка незачем.
  const preview = useQuery({
    queryKey: ['requestPreview', String(total), String(upfront), period, payments, lang],
    queryFn: () =>
      fetchRequestPreview({
        totalPrice: total.toString(),
        upfront: upfront.toString(),
        period,
        nPayments: payments,
        lang,
      }),
    enabled: problem === null,
    staleTime: 60_000,
  });

  const send = useMutation({
    mutationFn: () =>
      createRequest({
        initData: getInitData(),
        address: actions.myAddress,
        collection,
        nftAddress: item?.address ?? null,
        itemName: item?.name ?? null,
        totalPrice: total.toString(),
        upfront: upfront.toString(),
        period,
        nPayments: payments,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['myRequests'] });
      void qc.invalidateQueries({ queryKey: ['requests'] });
    },
  });

  if (send.isSuccess) {
    // Скольким людям это письмо ушло — то, от чего зависит, ждать ли ответа
    // сегодня. Ноль значит, что держателей этой коллекции в приложении пока
    // нет, и запрос будет лежать на доске.
    const notified = send.data.notified;
    const ownerNotified = send.data.ownerNotified;
    return (
      <div className="screen">
        {/* Что увидит продавец: человек называет цену вслепую, не понимая, как
          его предложение выглядит с той стороны. Текст собирает сервер тем
          же кодом, что пишет настоящее письмо. */}
      {preview.data !== undefined && (
        <div className="card col" style={{ background: 'var(--sf2)', borderColor: 'transparent' }}>
          <div className="label">{t('request.what_seller_sees')}</div>
          <span className="text" style={{ whiteSpace: 'pre-line' }}>{preview.data.text}</span>
        </div>
      )}

      <div className="card col">
          <strong style={{ fontSize: 17 }}>{t('request.sent')}</strong>
          <span className="muted" style={{ fontSize: 13 }}>
            {/* Именной оффер видит один человек, и говорить про «ещё N
                держателей» тут нечего: им он не показывается. */}
            {ownerNotified
              ? t('request.sent_to_owner')
              : t('request.sent_hint', { count: notified })}
          </span>
          <button className="btn" onClick={onDone}>{t('state.close')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="col" style={{ gap: 2 }}>
        <strong style={{ fontSize: 17 }}>{t('request.new')}</strong>
        <strong style={{ fontSize: 14 }}>
          {item?.name ?? nameOf(collection).name ?? shortAddress(collection)}
        </strong>
      </div>

      {/* Какой это оффер — первым блоком, до цены.
          Вид определяет всё дальнейшее: кто его увидит, кто ответит и что
          значит отказ. Раньше разница была строкой мелким шрифтом, и вид
          зависел от того, с какого экрана человек пришёл, — он про неё
          попросту не знал. */}
      <div
        className="card col"
        style={{ gap: 4, background: 'var(--lime-soft)', borderColor: 'transparent' }}
      >
        <div className="row">
          <strong style={{ fontSize: 14 }}>
            {item === null ? t('request.scope_collection') : t('request.scope_item')}
          </strong>
          <span className="label">
            {t('request.request_goes')}{' '}
            {item === null ? t('request.model') : t('request.goes_to_owner')}
          </span>
        </div>
        <span className="text" style={{ fontSize: 12.5 }}>
          {item === null ? t('request.scope_collection_hint') : t('request.scope_item_hint')}
        </span>
      </div>

      <div className="card col">
        <div className="row">
          <span className="muted">{t('request.willing_pay_total', { cur: CURRENCY })}</span>
          <span className="num">{sum(total)}</span>
        </div>
        <input
          className="mono"
          type="number"
          value={price}
          onChange={(e) => setPrice(Math.max(1, Number(e.target.value)))}
          style={{
            background: 'var(--sf2)',
            border: '1px solid var(--line)',
            color: 'var(--ink)',
            borderRadius: 10,
            padding: 10,
            fontSize: 18,
          }}
        />

        <div className="row">
          <span className="muted">{t('request.down_payment')}</span>
          <span className="num">{downPct}% · {money(upfront)}</span>
        </div>
        <input type="range" min={10} max={50} value={downPct} onChange={(e) => setDownPct(Number(e.target.value))} />

        <div className="row">
          <span className="muted">{t('request.term_weekly_payments')}</span>
          <span className="num">{payments} × {money(installment)}</span>
        </div>
        <input type="range" min={1} max={52} value={payments} onChange={(e) => setPayments(Number(e.target.value))} />

        <div className="row">
          <span className="muted">{t('request.every')}</span>
          <span className="num">{duration(period)}</span>
        </div>
        <input type="range" min={1} max={8} value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} />
      </div>

      <div className="card col">
        <div className="label">{t('request.what_seller_sees')}</div>
        <div className="row">
          <span className="muted">{t('counter.total')}</span>
          <span className="num">{sum(total)}</span>
        </div>
        <div className="row">
          <span className="muted">{t('counter.down')}</span>
          <span className="num">{sum(upfront)}</span>
        </div>
        <div className="row">
          <span className="muted">{t('counter.payments')}</span>
          <span className="num">{payments} × {money(installment)}</span>
        </div>
        <span className="muted" style={{ fontSize: 12 }}>{t('request.tier_is_sellers')}</span>
      </div>

      {problem !== null && <div className="card danger">{t(`err.${problem}`, { min: sum(MIN_PAYMENT) })}</div>}
      {send.error !== null && send.error !== undefined && (
        <div className="card danger">{errorText(send.error)}</div>
      )}
      {!actions.connected && <div className="card muted">{t('err.connect_wallet')}</div>}

      <button
        className="btn"
        disabled={send.isPending || problem !== null || !actions.connected}
        onClick={() => send.mutate()}
      >
        {send.isPending ? '…' : t('request.send')}
      </button>
      <button className="btn btn-ghost" onClick={onDone}>{t('state.close')}</button>
    </div>
  );
}
